import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  QueryList,
  Signal,
  signal,
  ViewChildren,
  WritableSignal
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { BottomNavbarComponent, HomeNavbarComponent, PageLayoutComponent } from '@ui-components';
import { Observable } from 'rxjs';
import { NotificationsListQueryHandler } from '@notifications';
import { GetTeamsQueryHandler, GetTeamsQueryResult } from '@teams';
import { AnalyticsService, AppRoutes, CreateActionModalService, DashboardAnalyticsEvent } from '@core';
import { DashboardNavbarComponent } from '../dashboard-navbar/dashboard-navbar.component';
import { DashboardGamesListComponent } from '../dashboard-games-list/dashboard-games-list.component';
import { DashboardInvitationsComponent } from '../dashboard-invitations/dashboard-invitations.component';
import { DashboardEmptyStateComponent } from '../dashboard-empty-state/dashboard-empty-state.component';
import { DashboardContactComponent } from '../dashboard-contact/dashboard-contact.component';
import { DashboardTeamsComponent } from '../dashboard-teams/dashboard-teams.component';
import { CreateTeamComponent } from '../create-team/create-team.component';
import { DashboardMessagesComponent } from '../dashboard-messages/dashboard-messages.component';

@Component({
  selector: 'lib-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    HomeNavbarComponent,
    BottomNavbarComponent,
    DashboardNavbarComponent,
    DashboardGamesListComponent,
    DashboardInvitationsComponent,
    DashboardEmptyStateComponent,
    DashboardContactComponent,
    DashboardTeamsComponent,
    CreateTeamComponent,
    DashboardMessagesComponent,
    PageLayoutComponent
  ],
  providers: [NotificationsListQueryHandler, GetTeamsQueryHandler, CreateActionModalService]
})
export class DashboardComponent {
  @ViewChildren(DashboardGamesListComponent) dashboardGamesListComponent!: QueryList<DashboardGamesListComponent>;

  private readonly notificationsListQueryHandler: NotificationsListQueryHandler = inject(NotificationsListQueryHandler);
  private readonly createActionModalService: CreateActionModalService = inject(CreateActionModalService);
  private readonly getTeamsQueryHandler: GetTeamsQueryHandler = inject(GetTeamsQueryHandler);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly router: Router = inject(Router);
  private readonly analyticsService: AnalyticsService = inject(AnalyticsService);

  private readonly isNewNotifications$: Observable<boolean> =
    this.notificationsListQueryHandler.hasUnreadNotifications();

  readonly isGameAvailable: WritableSignal<boolean> = signal<boolean>(true);
  readonly isInvitationsAvailable: WritableSignal<boolean> = signal<boolean>(true);
  readonly isUpcomingLoading: WritableSignal<boolean> = signal<boolean>(true);
  readonly teams: WritableSignal<GetTeamsQueryResult[]> = signal<GetTeamsQueryResult[]>([]);
  readonly isEmptyStateVisible: Signal<boolean> = computed<boolean>(() => {
    return !this.isUpcomingLoading() && !this.isGameAvailable() && !this.isInvitationsAvailable();
  });
  readonly isNewNotifications: Signal<boolean | undefined> = toSignal<boolean>(this.isNewNotifications$);
  readonly hasUpcomingGames: Signal<boolean> = computed<boolean>(
    () => this.isInvitationsAvailable() || this.isGameAvailable()
  );
  readonly invitationsCount: WritableSignal<number> = signal<number>(0);
  readonly gamesCount: WritableSignal<number> = signal<number>(0);
  readonly upcomingGamesCount: Signal<number> = computed<number>(() => this.invitationsCount() + this.gamesCount());

  constructor() {
    this.loadTeams();
  }

  goToGameCreator(): void {
    this.createActionModalService.open();
  }

  goToMyGames(): void {
    this.analyticsService.buttonClick(DashboardAnalyticsEvent.GamesAll, { gamesCount: this.upcomingGamesCount() });
    this.router.navigate([AppRoutes.MY_GAMES]);
  }

  setIsInvitationsAvailable(event: boolean): void {
    this.isInvitationsAvailable.set(event);
  }

  setIsGameAvailable(event: boolean): void {
    this.isGameAvailable.set(event);
  }

  setInvitationsCount(count: number): void {
    this.invitationsCount.set(count);
  }

  setGamesCount(count: number): void {
    this.gamesCount.set(count);
  }

  setUpcomingLoading(isLoading: boolean): void {
    this.isUpcomingLoading.set(isLoading);
  }

  invitationAccepted(): void {
    this.dashboardGamesListComponent?.forEach((child: DashboardGamesListComponent) => child?.refresh());
  }

  private loadTeams(): void {
    this.getTeamsQueryHandler
      .getTeams()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((teams: GetTeamsQueryResult[]) => this.teams.set(teams));
  }
}
